import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { taxFilingApi, ApiError } from '@solara/api-client';
import type { TaxFilingDto } from '@solara/api-client';

const URGENT_STATUSES = new Set(['AWAITING_CLIENT_APPROVAL']);

export default function TaxFilingsPage() {
  const { t } = useTranslation('p1');
  const [filings, setFilings] = useState<TaxFilingDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setFilings(await taxFilingApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  const urgent = filings.filter((f) => URGENT_STATUSES.has(f.status));
  const others = filings.filter((f) => !URGENT_STATUSES.has(f.status));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('taxFiling.titleList')}</h1>
        <p className="text-fg-muted text-sm">{t('taxFiling.subtitleList')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      {urgent.length > 0 && (
        <Card className="border-warning/40 bg-warning/5 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-warning">
              <AlertCircle className="h-4 w-4" />
              {t('taxFiling.awaitingYourApproval')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="divide-y divide-border">
              {urgent.map((f) => (
                <FilingRow key={f.id} filing={f} t={t} />
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t('taxFiling.allFilings')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {others.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('taxFiling.noFilings')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {others.map((f) => (
                <FilingRow key={f.id} filing={f} t={t} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function FilingRow({
  filing,
  t,
}: {
  filing: TaxFilingDto;
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  return (
    <li className="flex items-center justify-between py-3 text-sm">
      <div>
        <p className="font-medium">
          <Link to={`/tax/filings/${filing.id}`} className="hover:underline">
            {filing.filingType} · {filing.periodStart.slice(0, 10)} → {filing.periodEnd.slice(0, 10)}
          </Link>
        </p>
        <p className="text-xs text-fg-muted">
          {t('taxFiling.due', { date: new Date(filing.dueDate).toLocaleDateString() })} ·{' '}
          {t('taxFiling.netPayable', {
            amount: (filing.netPayableMinor / 100).toLocaleString(),
          })}
        </p>
      </div>
      <FilingStatusBadge status={filing.status} t={t} />
    </li>
  );
}

function FilingStatusBadge({
  status,
  t,
}: {
  status: string;
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const label = t(`taxFiling.statuses.${status}`, { defaultValue: status });
  if (status === 'ACCEPTED')
    return (
      <Badge tone="success" className="text-xs">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        {label}
      </Badge>
    );
  if (status === 'AWAITING_CLIENT_APPROVAL')
    return <Badge tone="warning" className="text-xs">{label}</Badge>;
  if (status === 'SUBMITTED') return <Badge tone="info" className="text-xs">{label}</Badge>;
  return <Badge tone="neutral" className="text-xs">{label}</Badge>;
}
